/**
 * LandingChoosePath — three-up "Choose your path" band on the homepage (Iter269).
 *
 * Reframes the buyer journey so visitors hit their lane in one click:
 *   • Developer   → /audit (One-Shot / Builder / Team subscriptions)
 *   • Risk Officer → /atlas + /labs/mpc-vendor-matrix (monitoring)
 *   • CISO / Lead → /pricing/audits#pqc-suite (PQC Audit Suite engagement)
 *
 * The headline PQC Audit Suite CTA sits above the cards so anyone who
 * already knows what they want can short-circuit into the bundle.
 */
import React from "react";
import { Link } from "react-router-dom";
import {
  Code, ShieldAlert, Building2, ArrowRight, Sparkles,
} from "lucide-react";


const PATHS = [
  {
    id: "developer",
    icon: Code,
    eyebrow: "DEVELOPER · ENG TEAM",
    title: "Audit my code",
    body:  "Ship contracts with a signed PQC + classical security audit. Self-serve from $149, $99/mo for active dev teams.",
    cta:   "Start a contract audit",
    to:    "/audit",
    accent: "var(--qt-cyan)",
    chips: ["$149 one-shot", "$99/mo Builder", "<90s turnaround"],
  },
  {
    id: "risk",
    icon: ShieldAlert,
    eyebrow: "RISK OFFICER · PROCUREMENT",
    title: "Monitor my portfolio",
    body:  "Atlas treasury monitoring + MPC Vendor Matrix subscription. Know which custody stack is HNDL-exposed before signing.",
    cta:   "See monitoring plans",
    to:    "/labs/mpc-vendor-matrix",
    accent: "#FFB000",
    chips: ["Atlas · subscription", "MPC Matrix · $5k/yr", "15+ vendors scored"],
  },
  {
    id: "ciso",
    icon: Building2,
    eyebrow: "CISO · PROTOCOL LEAD",
    title: "Commission a PQC audit",
    body:  "Signed deliverable engagements. Hybrid Signature, EIP-7702, MPC Wallet — bundle any 2 and save 15%, all 3 and save 25%.",
    cta:   "Open the PQC Audit Suite",
    to:    "/pricing/enterprise#pqc-suite",
    accent: "#1FDD83",
    chips: ["$12k–$45k engagements", "Suite: -25% on all 3", "Signed certificate"],
    featured: true,
  },
];


export default function LandingChoosePath() {
  return (
    <section className="max-w-[1400px] mx-auto px-6 sm:px-8 py-16"
             data-testid="landing-choose-path">
      <div className="text-center max-w-3xl mx-auto">
        <div className="qt-label inline-block"
             style={{ color: "#1FDD83" }}
             data-testid="choose-path-eyebrow">
          <Sparkles size={12} className="inline mr-1.5" />
          PICK YOUR PATH
        </div>
        <h2 className="qt-display text-4xl sm:text-5xl mt-3"
            style={{ color: "#fff", lineHeight: 1.1 }}
            data-testid="choose-path-headline">
          QorTrace has three buyers.{" "}
          <span style={{ color: "#1FDD83" }}>Which one are you?</span>
        </h2>
        <p className="mt-4" style={{ color: "var(--qt-text-2)", lineHeight: 1.6 }}>
          One platform · three buyer journeys · clear price for each. Devs
          self-serve. Risk teams subscribe to monitoring. CISOs commission
          signed audit engagements.
        </p>
      </div>

      {/* Headline Suite CTA — above the 3-up so power users skip the cards */}
      <Link to="/pricing/enterprise#pqc-suite"
            data-testid="choose-path-suite-cta"
            className="mt-8 inline-flex items-center gap-2 qt-mono"
            style={{
              fontSize: 13, padding: "0.7rem 1.1rem",
              borderRadius: 99,
              background: "linear-gradient(135deg, rgba(31,221,131,0.18), rgba(0,229,255,0.10))",
              border: "1px solid rgba(31,221,131,0.42)",
              color: "#1FDD83",
              fontWeight: 700, letterSpacing: "0.14em",
              cursor: "pointer",
              display: "block",
              width: "fit-content",
              margin: "1.75rem auto 0",
            }}>
        🆕 NEW · PQC AUDIT SUITE · -25% ON ALL 3 MOAT-DEEPENERS
        <ArrowRight size={14} className="inline ml-2" />
      </Link>

      {/* 3-up path grid */}
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-4"
           data-testid="choose-path-grid">
        {PATHS.map((p) => (
          <PathCard key={p.id} path={p} />
        ))}
      </div>
    </section>
  );
}


function PathCard({ path }) {
  const Icon = path.icon;
  return (
    <Link to={path.to}
          data-testid={`choose-path-card-${path.id}`}
          className="qt-card relative overflow-hidden transition-all"
          style={{
            border: path.featured
              ? `1px solid ${path.accent}`
              : "1px solid var(--qt-border)",
            background: path.featured
              ? "linear-gradient(135deg, rgba(31,221,131,0.08), transparent 70%)"
              : "rgba(7,10,18,0.55)",
            padding: "1.75rem 1.5rem",
            display: "flex", flexDirection: "column",
            textDecoration: "none",
            boxShadow: path.featured
              ? `0 18px 50px -28px ${path.accent}55`
              : "none",
          }}>

      {path.featured && (
        <div className="absolute top-3 right-3 qt-mono text-[9px]"
             style={{
               color: "#04060a", background: path.accent,
               padding: "3px 10px", borderRadius: 99,
               letterSpacing: "0.16em", fontWeight: 700,
             }}>
          MOST IMPACT
        </div>
      )}

      <div className="flex items-center gap-2">
        <div className="flex items-center justify-center rounded-full"
             style={{
               width: 38, height: 38,
               background: `${path.accent}22`,
               border: `1px solid ${path.accent}55`,
             }}>
          <Icon size={18} style={{ color: path.accent }} />
        </div>
      </div>

      <div className="qt-mono text-xs mt-3"
           style={{ color: path.accent, letterSpacing: "0.18em", fontWeight: 700 }}>
        {path.eyebrow}
      </div>
      <div className="qt-display text-2xl mt-2"
           style={{ color: "#fff", lineHeight: 1.15 }}>
        {path.title}
      </div>
      <p className="text-sm mt-3 flex-1"
         style={{ color: "var(--qt-text-2)", lineHeight: 1.55 }}>
        {path.body}
      </p>

      <div className="mt-4 flex flex-wrap gap-1.5">
        {path.chips.map((c) => (
          <span key={c}
                className="qt-mono text-[10px]"
                style={{
                  color: "var(--qt-text-2)",
                  letterSpacing: "0.08em",
                  padding: "3px 8px",
                  borderRadius: 99,
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid var(--qt-border)",
                }}>
            {c}
          </span>
        ))}
      </div>

      <div className="mt-5 inline-flex items-center gap-1.5 qt-mono text-xs font-bold"
           style={{ color: path.accent, letterSpacing: "0.10em" }}>
        {path.cta} <ArrowRight size={13} />
      </div>
    </Link>
  );
}
